// app-ui/src/config/colorThemes.ts
// Color theme configurations for the application

export type ColorTheme = 
    // Vibrant Gradients
    | 'purple-blue' 
    | 'green-emerald' 
    | 'orange-red' 
    | 'pink-rose' 
    | 'cyan-teal' 
    | 'indigo-violet'
    // Neutral Themes
    | 'classic-white'
    | 'pink'
    | 'neutral-gray'
    | 'warm-beige'
    // Additional Vibrant Options
    | 'blue-cyan'
    | 'purple-pink'
    | 'green-teal'
    | 'orange-amber'
    | 'red-pink'
    | 'yellow-orange'
    // Dark Themes
    | 'dark-purple'
    | 'dark-blue'
    | 'dark-green'
    | 'dark-orange'
    // Cool Themes
    | 'ocean-blue'
    | 'forest-green'
    | 'sunset-orange'
    | 'lavender-purple'
    | 'coral-pink'
    | 'mint-green'
    // Warm Themes
    | 'golden-yellow'
    | 'copper-orange'
    | 'rose-gold'
    | 'amber-warm'
    // Modern Themes
    | 'slate-gray'
    | 'midnight-blue'
    | 'emerald-jade'
    | 'violet-indigo';

export interface ThemeConfig {
    primary: string;
    secondary: string;
    gradient: string;
    hoverGradient: string;
    ring: string;
    shadow: string;
    displayName: string;
}

export const colorThemes: Record<ColorTheme, ThemeConfig> = {
    // Vibrant Gradients
    'purple-blue': {
        primary: 'from-purple-500 to-blue-600',
        secondary: 'from-purple-600 to-blue-700',
        gradient: 'from-purple-500 to-blue-600',
        hoverGradient: 'hover:from-purple-600 hover:to-blue-700',
        ring: 'focus:ring-purple-500',
        shadow: 'shadow-purple-500/25',
        displayName: 'Purple Blue',
    },
    'green-emerald': {
        primary: 'from-green-500 to-emerald-600',
        secondary: 'from-green-600 to-emerald-700',
        gradient: 'from-green-500 to-emerald-600',
        hoverGradient: 'hover:from-green-600 hover:to-emerald-700',
        ring: 'focus:ring-green-500',
        shadow: 'shadow-green-500/25',
        displayName: 'Green Emerald',
    },
    'orange-red': {
        primary: 'from-orange-500 to-red-600',
        secondary: 'from-orange-600 to-red-700',
        gradient: 'from-orange-500 to-red-600',
        hoverGradient: 'hover:from-orange-600 hover:to-red-700',
        ring: 'focus:ring-orange-500',
        shadow: 'shadow-orange-500/25',
        displayName: 'Orange Red',
    },
    'pink-rose': {
        primary: 'from-pink-500 to-rose-600',
        secondary: 'from-pink-600 to-rose-700',
        gradient: 'from-pink-500 to-rose-600',
        hoverGradient: 'hover:from-pink-600 hover:to-rose-700',
        ring: 'focus:ring-pink-500',
        shadow: 'shadow-pink-500/25',
        displayName: 'Pink Rose',
    },
    'cyan-teal': {
        primary: 'from-cyan-500 to-teal-600',
        secondary: 'from-cyan-600 to-teal-700',
        gradient: 'from-cyan-500 to-teal-600',
        hoverGradient: 'hover:from-cyan-600 hover:to-teal-700',
        ring: 'focus:ring-cyan-500',
        shadow: 'shadow-cyan-500/25',
        displayName: 'Cyan Teal',
    },
    'indigo-violet': {
        primary: 'from-indigo-500 to-violet-600',
        secondary: 'from-indigo-600 to-violet-700',
        gradient: 'from-indigo-500 to-violet-600',
        hoverGradient: 'hover:from-indigo-600 hover:to-violet-700',
        ring: 'focus:ring-indigo-500',
        shadow: 'shadow-indigo-500/25',
        displayName: 'Indigo Violet',
    },
    
    // Neutral Themes
    'classic-white': {
        primary: 'from-gray-100 to-gray-200',
        secondary: 'from-gray-200 to-gray-300',
        gradient: 'from-gray-100 to-gray-200',
        hoverGradient: 'hover:from-gray-200 hover:to-gray-300',
        ring: 'focus:ring-gray-400',
        shadow: 'shadow-gray-500/25',
        displayName: 'Classic White',
    },
    'pink': {
        primary: 'from-pink-400 to-pink-500',
        secondary: 'from-pink-500 to-pink-600',
        gradient: 'from-pink-400 to-pink-500',
        hoverGradient: 'hover:from-pink-500 hover:to-pink-600',
        ring: 'focus:ring-pink-400',
        shadow: 'shadow-pink-500/25',
        displayName: 'Pink',
    },
    'neutral-gray': {
        primary: 'from-gray-500 to-gray-600',
        secondary: 'from-gray-600 to-gray-700',
        gradient: 'from-gray-500 to-gray-600',
        hoverGradient: 'hover:from-gray-600 hover:to-gray-700',
        ring: 'focus:ring-gray-500',
        shadow: 'shadow-gray-500/25',
        displayName: 'Neutral Gray',
    },
    'warm-beige': {
        primary: 'from-amber-200 to-orange-200',
        secondary: 'from-amber-300 to-orange-300',
        gradient: 'from-amber-200 to-orange-200',
        hoverGradient: 'hover:from-amber-300 hover:to-orange-300',
        ring: 'focus:ring-amber-400',
        shadow: 'shadow-amber-500/25',
        displayName: 'Warm Beige',
    },
    
    // Additional Vibrant Options
    'blue-cyan': {
        primary: 'from-blue-500 to-cyan-500',
        secondary: 'from-blue-600 to-cyan-600',
        gradient: 'from-blue-500 to-cyan-500',
        hoverGradient: 'hover:from-blue-600 hover:to-cyan-600',
        ring: 'focus:ring-blue-500',
        shadow: 'shadow-blue-500/25',
        displayName: 'Blue Cyan',
    },
    'purple-pink': {
        primary: 'from-purple-500 to-pink-500',
        secondary: 'from-purple-600 to-pink-600',
        gradient: 'from-purple-500 to-pink-500',
        hoverGradient: 'hover:from-purple-600 hover:to-pink-600',
        ring: 'focus:ring-purple-500',
        shadow: 'shadow-purple-500/25',
        displayName: 'Purple Pink',
    },
    'green-teal': {
        primary: 'from-green-400 to-teal-500',
        secondary: 'from-green-500 to-teal-600',
        gradient: 'from-green-400 to-teal-500',
        hoverGradient: 'hover:from-green-500 hover:to-teal-600',
        ring: 'focus:ring-green-500',
        shadow: 'shadow-green-500/25',
        displayName: 'Green Teal',
    },
    'orange-amber': {
        primary: 'from-orange-400 to-amber-500',
        secondary: 'from-orange-500 to-amber-600',
        gradient: 'from-orange-400 to-amber-500',
        hoverGradient: 'hover:from-orange-500 hover:to-amber-600',
        ring: 'focus:ring-orange-500',
        shadow: 'shadow-orange-500/25',
        displayName: 'Orange Amber',
    },
    'red-pink': {
        primary: 'from-red-500 to-pink-500',
        secondary: 'from-red-600 to-pink-600',
        gradient: 'from-red-500 to-pink-500',
        hoverGradient: 'hover:from-red-600 hover:to-pink-600',
        ring: 'focus:ring-red-500',
        shadow: 'shadow-red-500/25',
        displayName: 'Red Pink',
    },
    'yellow-orange': {
        primary: 'from-yellow-400 to-orange-500',
        secondary: 'from-yellow-500 to-orange-600',
        gradient: 'from-yellow-400 to-orange-500',
        hoverGradient: 'hover:from-yellow-500 hover:to-orange-600',
        ring: 'focus:ring-yellow-500',
        shadow: 'shadow-yellow-500/25',
        displayName: 'Yellow Orange',
    },
    
    // Dark Themes
    'dark-purple': {
        primary: 'from-purple-700 to-purple-900',
        secondary: 'from-purple-800 to-purple-950',
        gradient: 'from-purple-700 to-purple-900',
        hoverGradient: 'hover:from-purple-800 hover:to-purple-950',
        ring: 'focus:ring-purple-700',
        shadow: 'shadow-purple-700/25',
        displayName: 'Dark Purple',
    },
    'dark-blue': {
        primary: 'from-blue-700 to-blue-900',
        secondary: 'from-blue-800 to-blue-950',
        gradient: 'from-blue-700 to-blue-900',
        hoverGradient: 'hover:from-blue-800 hover:to-blue-950',
        ring: 'focus:ring-blue-700',
        shadow: 'shadow-blue-700/25',
        displayName: 'Dark Blue',
    },
    'dark-green': {
        primary: 'from-green-700 to-green-900',
        secondary: 'from-green-800 to-green-950',
        gradient: 'from-green-700 to-green-900',
        hoverGradient: 'hover:from-green-800 hover:to-green-950',
        ring: 'focus:ring-green-700',
        shadow: 'shadow-green-700/25',
        displayName: 'Dark Green',
    },
    'dark-orange': {
        primary: 'from-orange-700 to-orange-900',
        secondary: 'from-orange-800 to-orange-950',
        gradient: 'from-orange-700 to-orange-900',
        hoverGradient: 'hover:from-orange-800 hover:to-orange-950',
        ring: 'focus:ring-orange-700',
        shadow: 'shadow-orange-700/25',
        displayName: 'Dark Orange',
    },
    
    // Cool Themes
    'ocean-blue': {
        primary: 'from-blue-400 to-cyan-400',
        secondary: 'from-blue-500 to-cyan-500',
        gradient: 'from-blue-400 to-cyan-400',
        hoverGradient: 'hover:from-blue-500 hover:to-cyan-500',
        ring: 'focus:ring-blue-400',
        shadow: 'shadow-blue-400/25',
        displayName: 'Ocean Blue',
    },
    'forest-green': {
        primary: 'from-green-600 to-emerald-700',
        secondary: 'from-green-700 to-emerald-800',
        gradient: 'from-green-600 to-emerald-700',
        hoverGradient: 'hover:from-green-700 hover:to-emerald-800',
        ring: 'focus:ring-green-600',
        shadow: 'shadow-green-600/25',
        displayName: 'Forest Green',
    },
    'sunset-orange': {
        primary: 'from-orange-400 to-red-500',
        secondary: 'from-orange-500 to-red-600',
        gradient: 'from-orange-400 to-red-500',
        hoverGradient: 'hover:from-orange-500 hover:to-red-600',
        ring: 'focus:ring-orange-400',
        shadow: 'shadow-orange-400/25',
        displayName: 'Sunset Orange',
    },
    'lavender-purple': {
        primary: 'from-purple-300 to-purple-500',
        secondary: 'from-purple-400 to-purple-600',
        gradient: 'from-purple-300 to-purple-500',
        hoverGradient: 'hover:from-purple-400 hover:to-purple-600',
        ring: 'focus:ring-purple-300',
        shadow: 'shadow-purple-300/25',
        displayName: 'Lavender Purple',
    },
    'coral-pink': {
        primary: 'from-pink-300 to-rose-400',
        secondary: 'from-pink-400 to-rose-500',
        gradient: 'from-pink-300 to-rose-400',
        hoverGradient: 'hover:from-pink-400 hover:to-rose-500',
        ring: 'focus:ring-pink-300',
        shadow: 'shadow-pink-300/25',
        displayName: 'Coral Pink',
    },
    'mint-green': {
        primary: 'from-green-300 to-emerald-400',
        secondary: 'from-green-400 to-emerald-500',
        gradient: 'from-green-300 to-emerald-400',
        hoverGradient: 'hover:from-green-400 hover:to-emerald-500',
        ring: 'focus:ring-green-300',
        shadow: 'shadow-green-300/25',
        displayName: 'Mint Green',
    },
    
    // Warm Themes
    'golden-yellow': {
        primary: 'from-yellow-400 to-amber-500',
        secondary: 'from-yellow-500 to-amber-600',
        gradient: 'from-yellow-400 to-amber-500',
        hoverGradient: 'hover:from-yellow-500 hover:to-amber-600',
        ring: 'focus:ring-yellow-400',
        shadow: 'shadow-yellow-400/25',
        displayName: 'Golden Yellow',
    },
    'copper-orange': {
        primary: 'from-orange-500 to-amber-600',
        secondary: 'from-orange-600 to-amber-700',
        gradient: 'from-orange-500 to-amber-600',
        hoverGradient: 'hover:from-orange-600 hover:to-amber-700',
        ring: 'focus:ring-orange-500',
        shadow: 'shadow-orange-500/25',
        displayName: 'Copper Orange',
    },
    'rose-gold': {
        primary: 'from-rose-400 to-amber-400',
        secondary: 'from-rose-500 to-amber-500',
        gradient: 'from-rose-400 to-amber-400',
        hoverGradient: 'hover:from-rose-500 hover:to-amber-500',
        ring: 'focus:ring-rose-400',
        shadow: 'shadow-rose-400/25',
        displayName: 'Rose Gold',
    },
    'amber-warm': {
        primary: 'from-amber-400 to-orange-400',
        secondary: 'from-amber-500 to-orange-500',
        gradient: 'from-amber-400 to-orange-400',
        hoverGradient: 'hover:from-amber-500 hover:to-orange-500',
        ring: 'focus:ring-amber-400',
        shadow: 'shadow-amber-400/25',
        displayName: 'Amber Warm',
    },
    
    // Modern Themes
    'slate-gray': {
        primary: 'from-slate-500 to-slate-700',
        secondary: 'from-slate-600 to-slate-800',
        gradient: 'from-slate-500 to-slate-700',
        hoverGradient: 'hover:from-slate-600 hover:to-slate-800',
        ring: 'focus:ring-slate-500',
        shadow: 'shadow-slate-500/25',
        displayName: 'Slate Gray',
    },
    'midnight-blue': {
        primary: 'from-blue-800 to-indigo-900',
        secondary: 'from-blue-900 to-indigo-950',
        gradient: 'from-blue-800 to-indigo-900',
        hoverGradient: 'hover:from-blue-900 hover:to-indigo-950',
        ring: 'focus:ring-blue-800',
        shadow: 'shadow-blue-800/25',
        displayName: 'Midnight Blue',
    },
    'emerald-jade': {
        primary: 'from-emerald-500 to-teal-600',
        secondary: 'from-emerald-600 to-teal-700',
        gradient: 'from-emerald-500 to-teal-600',
        hoverGradient: 'hover:from-emerald-600 hover:to-teal-700',
        ring: 'focus:ring-emerald-500',
        shadow: 'shadow-emerald-500/25',
        displayName: 'Emerald Jade',
    },
    'violet-indigo': {
        primary: 'from-violet-500 to-indigo-600',
        secondary: 'from-violet-600 to-indigo-700',
        gradient: 'from-violet-500 to-indigo-600',
        hoverGradient: 'hover:from-violet-600 hover:to-indigo-700',
        ring: 'focus:ring-violet-500',
        shadow: 'shadow-violet-500/25',
        displayName: 'Violet Indigo',
    },
};

// Helper function to get all theme keys
export const getAllThemes = (): ColorTheme[] => {
    return Object.keys(colorThemes) as ColorTheme[];
};

// Helper function to get theme config
export const getThemeConfig = (theme: ColorTheme): ThemeConfig => {
    return colorThemes[theme];
};

